import { useMemo, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import {
  Settings,
  ChevronDown,
  Briefcase,
  User,
  Lock,
  Users,
  CreditCard,
  BarChart2,
  Link2,
  LogOut,
  X,
  FileText,
  Wallet,
} from "lucide-react";

import { Avatar } from "../Avatar";
import "./ProfileSettingsPanel.scss";

const ICON_BY_TEXT = {
  "My Connections": Link2,
  Settings: Settings,
  Accounts: User,
  "Manage Jobs": Briefcase,
  "Manage Resumes": Briefcase,
  "Edit Profile": User,
  "Edit Password": Lock,
  "Manage Users": Users,
  Payment: CreditCard,
  "Status of Open Positions": BarChart2,
  "Saved Cards": CreditCard,
  "Transaction History": FileText,
  "Payment plan/Pay for services": Wallet,
  "View Cost": BarChart2,
  "Buy Services": Wallet,
};

function getIconForItem(item) {
  if (ICON_BY_TEXT[item.text]) {
    return ICON_BY_TEXT[item.text];
  }
  return FileText;
}

/** Stable open-state keys by tree position. Route `id`s collide in JS (e.g. 9.1_0 → 9.1). */
function pathToKey(pathSegments) {
  return `menu-${pathSegments.join("-")}`;
}

function MenuItem({
  item,
  depth = 0,
  pathSegments,
  openGroups,
  toggleGroup,
  onLeafNavigate,
}) {
  const Icon = getIconForItem(item);
  const isGroup = item.children && item.children.length > 0;
  const key = pathToKey(pathSegments);
  const isOpen = !!openGroups[key];
  const depthPadClass =
    depth === 0
      ? "profile-settings-panel__pad-d0"
      : depth === 1
        ? "profile-settings-panel__pad-d1"
        : "profile-settings-panel__pad-d2";

  if (isGroup) {
    return (
      <div className="profile-settings-panel__group">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toggleGroup(key);
          }}
          className={`profile-settings-panel__row profile-settings-panel__row--group ${depthPadClass}${
            depth >= 1 ? " profile-settings-panel__nested-group" : ""
          } ${isOpen ? "is-open" : ""}`}
        >
          <div className="profile-settings-panel__row-inner">
            <Icon
              className="profile-settings-panel__icon"
              aria-hidden
              strokeWidth={2}
            />
            <span className="profile-settings-panel__label profile-settings-panel__label--group">
              {item.text}
            </span>
          </div>
          <ChevronDown
            className={`profile-settings-panel__chevron-down ${isOpen ? "is-rotated" : ""}`}
            aria-hidden
            strokeWidth={2}
          />
        </button>

        <div
          className={`profile-settings-panel__children-wrap ${isOpen ? "is-expanded" : ""}`}
        >
          <div
            className={`profile-settings-panel__children ${
              depth >= 1 ? "profile-settings-panel__children--nested" : ""
            }`}
          >
            {item.children.map((child, idx) => (
              <MenuItem
                key={pathToKey([...pathSegments, idx])}
                item={child}
                depth={depth + 1}
                pathSegments={[...pathSegments, idx]}
                openGroups={openGroups}
                toggleGroup={toggleGroup}
                onLeafNavigate={onLeafNavigate}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!item.link) {
    return null;
  }

  return (
    <NavLink
      to={item.link}
      onClick={onLeafNavigate}
      className={({ isActive }) =>
        `profile-settings-panel__row profile-settings-panel__row--leaf ${depthPadClass} ${
          isActive ? "is-active" : ""
        }`
      }
    >
      <div className="profile-settings-panel__row-inner">
        <Icon
          className="profile-settings-panel__icon"
          aria-hidden
          strokeWidth={2}
        />
        <span className="profile-settings-panel__label">{item.text}</span>
      </div>
    </NavLink>
  );
}

function buildDefaultOpenGroups(submenus) {
  const items = submenus.filter((s) => !s.isLogout);
  const open = {};
  const walk = (list, prefix = []) => {
    list.forEach((it, idx) => {
      const segs = [...prefix, idx];
      // Only Settings expanded; Accounts / Payment stay collapsed until clicked
      if (it.text === "Settings") {
        open[pathToKey(segs)] = true;
      }
      if (it.children) walk(it.children, segs);
    });
  };
  walk(items);
  return open;
}

export default function ProfileSettingsPanel({
  submenus,
  userData,
  getAvatarUrl,
  getDisplayName,
  profilePath,
  onClose,
}) {
  const [openGroups, setOpenGroups] = useState(() =>
    buildDefaultOpenGroups(submenus)
  );

  const toggleGroup = (key) => {
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const { regularMenus, logoutMenu } = useMemo(() => {
    const regular = submenus.filter((s) => !s.isLogout);
    const logout = submenus.find((s) => s.isLogout);
    return { regularMenus: regular, logoutMenu: logout };
  }, [submenus]);

  const onLeafNavigate = () => {
    if (onClose) onClose();
  };

  return (
    <div className="profile-settings-panel">
      <div className="profile-settings-panel__header">
        <div className="profile-settings-panel__header-row">
          <Link
            to={profilePath}
            className="profile-settings-panel__user-block"
            onClick={onLeafNavigate}
          >
            <div className="profile-settings-panel__avatar-wrap">
              <Avatar
                src={getAvatarUrl()}
                username={getDisplayName()}
                size="lg"
                center={false}
              />
            </div>
            <div className="profile-settings-panel__user-text">
              <p className="profile-settings-panel__name">{getDisplayName()}</p>
              <p className="profile-settings-panel__email">{userData?.email}</p>
            </div>
          </Link>
          {onClose && (
            <button
              type="button"
              className="profile-settings-panel__close"
              onClick={onClose}
              aria-label="Close menu"
            >
              <X className="profile-settings-panel__close-icon" strokeWidth={2} />
            </button>
          )}
        </div>
      </div>

      <nav className="profile-settings-panel__nav">
        {regularMenus.map((item, index) => (
          <MenuItem
            key={pathToKey([index])}
            item={item}
            depth={0}
            pathSegments={[index]}
            openGroups={openGroups}
            toggleGroup={toggleGroup}
            onLeafNavigate={onLeafNavigate}
          />
        ))}
      </nav>

      {logoutMenu && (
        <>
          <div className="profile-settings-panel__divider" />
          <div className="profile-settings-panel__footer">
            <NavLink
              to={logoutMenu.link}
              className="profile-settings-panel__logout"
              onClick={onLeafNavigate}
            >
              <LogOut
                className="profile-settings-panel__logout-icon"
                strokeWidth={2}
                aria-hidden
              />
              <span className="profile-settings-panel__logout-label">
                {logoutMenu.text}
              </span>
            </NavLink>
          </div>
        </>
      )}
    </div>
  );
}
